import { useEffect, useState } from "react"
import * as S from "./styled"
import Header from "../../components/Header"
import Footer from "../../components/Footer"
import Container from "../../components/Container"
import getStore from "../../store"
import { Api } from "../../api"
import { TOrganizer } from "../../utils/@types/data/organizer"
import { sellerContact, sellerName } from "../../utils/tb/supportContact"

import { ReactComponent as WhatsappIcon } from "../../assets/icons/whatsapp.svg"
import { Phone } from "lucide-react"

type TFaq = {
  question: string
  answer: JSX.Element
  /** Mostra o botão "Recuperar meu ingresso" no fim da resposta. */
  withRecovery?: boolean
}

const RESEND_OR_DOWNLOAD = (
  <>
    <strong>Re-Enviar para WhatsApp</strong> ou <strong>Baixar em PDF</strong>
  </>
)

const FAQ: TFaq[] = [
  {
    question: "Não recebi meu ingresso. O que faço?",
    answer: (
      <p>
        Recupere seu ingresso utilizando o mesmo número de telefone utilizado na
        compra. Faça o acesso no link abaixo e utilize as opções de{" "}
        {RESEND_OR_DOWNLOAD}.
      </p>
    ),
    withRecovery: true,
  },
  {
    question: "Meu número de telefone não foi encontrado. O que faço?",
    answer: (
      <p>
        Se ao realizar a compra você cadastrou um número errado, entre em
        contato com o produtor do evento solicitando uma 2ª via do seu
        ingresso.
      </p>
    ),
  },
  {
    question: "Quero recuperar meu ingresso",
    answer: (
      <p>
        Recupere seu ingresso utilizando o mesmo número de telefone utilizado na
        compra. Faça o acesso no link abaixo e utilize as opções de{" "}
        {RESEND_OR_DOWNLOAD}.
      </p>
    ),
    withRecovery: true,
  },
  {
    question: "Quero cancelar minha compra",
    answer: (
      <p>
        Cancelamentos e reembolsos são tratados conforme as regras definidas
        pelo produtor responsável do evento. Consulte o produtor para
        solicitação.
      </p>
    ),
  },
  {
    question: "Tenho dúvidas sobre o evento",
    answer: (
      <p>
        Horário, local, atrações, classificação, estacionamento e demais
        informações são de responsabilidade do produtor/organizador. Consulte os
        canais oficiais do evento.
      </p>
    ),
  },
  {
    question: "Preciso imprimir meu ingresso?",
    answer: (
      <p>
        Não é necessário! O QR Code pode ser apresentado diretamente pelo
        celular, mas, se preferir, pode imprimir em uma impressora comum, em
        papel A4 com fundo branco.
      </p>
    ),
  },
  {
    question: "Posso enviar meu ingresso para outra pessoa?",
    answer: (
      <p>
        Sim. Mas evite publicar ou compartilhar seu QR Code em locais públicos.
      </p>
    ),
  },
  {
    question: "Meu pagamento foi realizado, mas não encontrei o ingresso",
    answer: (
      <p>
        Tente recuperar o ingresso utilizando seu telefone informado na compra.
        Se ainda não localizar, entre em contato com o produtor responsável do
        evento e informe o ocorrido.
      </p>
    ),
    withRecovery: true,
  },
]

const TicketIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5h13A2.5 2.5 0 0 1 21 7.5v9a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 16.5z" />
    <path d="M8 5v14M16 5v14" />
    <path d="M10.5 10.25h3M10.5 13.75h3" />
  </svg>
)

/**
 * Página de suporte da loja. Os dados de "Responsável pela loja" são do
 * cliente (organizador) dono da loja: vêm do getInfo (em cache desde o boot),
 * com ou sem evento escolhido.
 */
const Support = () => {
  const { event, user } = getStore()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [organizer, setOrganizer] = useState<TOrganizer | null>(null)

  useEffect(() => {
    let alive = true
    Api.get.events({}).then((res) => {
      if (alive && res.ok && res.data.organizer) setOrganizer(res.data.organizer)
    })
    return () => {
      alive = false
    }
  }, [])

  // Sem evento escolhido, o store guarda a resposta inteira do getInfo (os
  // dados ficam em .info); o organizador é a fonte certa nos dois casos.
  const sellerInfo = organizer ?? event
  const seller = sellerName(sellerInfo)
  const contact = sellerContact(sellerInfo?.phone)
  // Logado na conta: direto para Meus ingressos; senão, o login por código.
  const recoveryTo = user && !user.guest ? "/mytickets" : "/login"

  return (
    <S.Page>
      <Header customData={organizer ?? undefined} />

      <Container fullHeight={true}>
        <S.Main>
          <S.Hero>Como podemos ajudar?</S.Hero>

          {(seller || contact) && (
            <S.SellerBox aria-label="Responsável pela loja de ingressos">
              <S.SellerLabel>Responsável pela loja de ingressos</S.SellerLabel>
              {seller && (
                <S.SellerName>
                  Essa loja de ingressos é de responsabilidade de{" "}
                  <strong>{seller}</strong>
                </S.SellerName>
              )}
              {contact && (
                <S.SellerContact
                  href={contact.href}
                  {...(contact.external
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  {contact.external ? (
                    <WhatsappIcon aria-hidden="true" />
                  ) : (
                    <Phone aria-hidden="true" />
                  )}
                  Telefone para contato: {contact.label}
                </S.SellerContact>
              )}
            </S.SellerBox>
          )}

          <S.Recovery>
            <S.RecoveryIcon aria-hidden="true">
              <TicketIcon />
            </S.RecoveryIcon>
            <S.RecoveryContent>
              <S.Kicker>Acesso rápido</S.Kicker>
              <h2>Não recebi meus ingressos / Quero recuperar</h2>
              <p>
                Recupere seus ingressos utilizando o mesmo número de telefone
                informado na compra. Depois, escolha entre {RESEND_OR_DOWNLOAD}.
              </p>
            </S.RecoveryContent>
            <S.RecoveryButton to={recoveryTo}>
              Recuperar meus ingressos
              <span aria-hidden="true">→</span>
            </S.RecoveryButton>
          </S.Recovery>

          <section aria-labelledby="faq-title">
            <S.SectionTitle id="faq-title">Dúvidas frequentes</S.SectionTitle>
            <S.FaqList>
              {FAQ.map((item, index) => {
                const open = openIndex === index
                const answerId = `faq-answer-${index}`
                return (
                  <S.FaqItem key={item.question}>
                    <S.FaqQuestion
                      type="button"
                      $open={open}
                      aria-expanded={open}
                      aria-controls={answerId}
                      onClick={() => setOpenIndex(open ? null : index)}
                    >
                      <span>{item.question}</span>
                      <span className="sign" aria-hidden="true">
                        +
                      </span>
                    </S.FaqQuestion>
                    {open && (
                      <S.FaqAnswer id={answerId} role="region">
                        {item.answer}
                        {item.withRecovery && (
                          <S.FaqAction to={recoveryTo}>
                            Recuperar meu ingresso
                          </S.FaqAction>
                        )}
                      </S.FaqAnswer>
                    )}
                  </S.FaqItem>
                )
              })}
            </S.FaqList>
          </section>

          <S.ImportantAlert>
            <span className="icon" aria-hidden="true">
              ⚠️
            </span>
            <div>
              <strong>Importante:</strong> ListaPix é a plataforma que fornece a
              tecnologia utilizada pelo produtor do evento para vender
              ingressos. Os ingressos são comprados e pagos diretamente para o
              produtor do evento. O produtor é o responsável pela venda,
              organização e realização do evento.
            </div>
          </S.ImportantAlert>
        </S.Main>
      </Container>

      <Footer customData={organizer ?? undefined} />
    </S.Page>
  )
}

export default Support
